import NeptuneElement from "../../NeptuneElement.js";


/**
 * Avatar component that displays user initials or a default user icon. The size of the avatar can be customized using the `size` attribute, which accepts values such as `xs`, `sm`, `md`, `lg`, and `xl`. If the `initials` attribute is provided, the avatar will display the initials; otherwise, it will show a default user icon.
 *
 * @element neptune-avatar
 * @attribute {string} size - The size of the avatar. Acceptable values are `xs`, `sm`, `md`, `lg`, and `xl`. This attribute is required.
 * @attribute {string} initials - The initials to display inside the avatar. If not provided, a default user icon will be shown.
 */
class Avatar extends NeptuneElement {

    static AVATAR_SIZES = ['xs', 'sm', 'md', 'lg', 'xl'];

    templateMarkup = `
        <span>
        <span hidden></span>
        <i class="fa-solid fa-user" aria-hidden="true" hidden></i>
        </span>
   `;
    componentCssUrl = '/src/components/core/avatar/avatar.css';


    /**
     * Initializes the Avatar component, validating required properties and processing initial attributes after rendering.
     * This method is called when the element is added to the DOM.
     * 
     * @returns {Promise<void>}
     */
    async connectedCallback() {
        await super.connectedCallback();
        this.validateRequiredProperties(['size']);
        this.processInitialAttributesAfterRender(Avatar.observedAttributes);
    }

    /**
     * Cleans up the Avatar component when it is removed from the DOM. This method is called when the element is disconnected from the DOM.
     * 
     * @returns {Promise<void>}
     */
    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    /**
     * Specifies the attributes to observe for changes. When any of these attributes change, the `attributeChangedCallback` method will be invoked.
     * 
     * @returns {string[]} An array of attribute names to observe.
     */
    static get observedAttributes() {
        return ['size', 'initials'];
    }

    /**
     * Called when an observed attribute is added, removed, or changed. This method processes the attribute change if the component has been rendered.
     * 
     * @param {string} name - The name of the attribute that changed.
     * @param {string|null} oldValue - The previous value of the attribute.
     * @param {string|null} newValue - The new value of the attribute.
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    /**
     * Processes the attribute change for the Avatar component.
     * 
     * @param {string} name - The name of the attribute that changed.
     * @param {string|null} oldValue - The previous value of the attribute.
     * @param {string|null} newValue - The new value of the attribute.
     */
    processAttribute(name, oldValue, newValue) {
        switch (name) {
            case 'size':
                this.validatePropertyValue(name, newValue, Avatar.AVATAR_SIZES);
                this.#renderAvatar();
                break;
            case 'initials':
                this.#renderAvatar();
                break;
            default:
                break;
        }
    }

    /**
     * Gets the value of the `initials` attribute.
     * 
     * @returns {string|null} The initials to display inside the avatar, or null if not set.
     */
    get initials() {
        return this.getAttribute('initials');
    }

    /**
     * Renders the avatar based on the current `initials` attribute. If initials are provided, they will be displayed; otherwise, a default user icon will be shown. This method updates the DOM elements accordingly.
     * 
     * @returns {void}
     */
    #renderAvatar() {
        const avatarSpan = this.shadowRoot.querySelector('span');
        const initialsSpan = avatarSpan?.querySelector('span');
        const iconElement = avatarSpan?.querySelector('i');
        if (!avatarSpan || !initialsSpan || !iconElement) {
            return;
        }
        if (this.initials) {
            initialsSpan.textContent = this.initials;
            initialsSpan.hidden = false;
            iconElement.hidden = true;
        } else {
            initialsSpan.textContent = '';
            initialsSpan.hidden = true;
            iconElement.hidden = false;
        }
    }
}

customElements.define('neptune-avatar', Avatar);
export default Avatar;